/**
 * Decisions: the cycle's open questions (issues labelled needs-discussion),
 * each with the options its issue offers and the outcome the room agrees,
 * plus decisions the room states that no card carries.
 */

import { useState } from 'react'
import {
  Badge,
  Button,
  Caption1,
  Input,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components'
import {
  Add16Regular,
  Dismiss12Regular,
  Gavel16Regular,
  Megaphone16Regular,
  Send16Regular,
} from '@fluentui/react-icons'
import { useToast } from '../ToastProvider'
import { usePostIssueComment } from '../../hooks/usePlanning'
import { apiErrorMessage } from '../../lib/apiError'
import { inertForGitHub } from '../../lib/planning'
import type { Plan, PlanEdit, PlanQuestion, PlanState } from '../../types/planning'
import { IssueNote, isAxiosStatus } from './IssueNote'
import { CommitInput, IssueLink, usePanelStyles } from './PlanParts'

const useStyles = makeStyles({
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: tokens.spacingHorizontalM,
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingHorizontalM,
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  decided: {
    border: `1px solid ${tokens.colorPaletteGreenBorder2}`,
    boxShadow: `inset 3px 0 0 ${tokens.colorPaletteGreenBorder2}`,
  },
  top: { display: 'flex', alignItems: 'center', gap: '6px' },
  title: {
    margin: 0,
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
  },
  options: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: tokens.spacingHorizontalS,
  },
  option: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '6px',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalS}`,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    background: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    fontFamily: 'inherit',
    fontSize: tokens.fontSizeBase300,
    textAlign: 'start',
    cursor: 'pointer',
    ':hover': { background: tokens.colorNeutralBackground1Hover },
    ':focus-visible': { outline: `2px solid ${tokens.colorStrokeFocus2}` },
  },
  chosen: {
    background: tokens.colorBrandBackground2,
    border: `1px solid ${tokens.colorBrandStroke1}`,
    color: tokens.colorBrandForeground1,
    fontWeight: tokens.fontWeightSemibold,
    ':hover': { background: tokens.colorBrandBackground2Hover },
  },
  star: { color: tokens.colorPaletteMarigoldForeground1 },
  foot: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  outcome: { flexGrow: 1 },
  said: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    justifyContent: 'space-between',
  },
  form: { display: 'flex', gap: tokens.spacingHorizontalS },
  grow: { flexGrow: 1 },
  srOnly: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
  },
})

function Question({
  q,
  plan,
  state,
  edit,
  cycle,
}: {
  q: PlanQuestion
  plan: Plan
  state: PlanState
  edit: (e: PlanEdit) => void
  cycle: string
}) {
  const styles = useStyles()
  const { showToast } = useToast()
  const post = usePostIssueComment(cycle)
  const id = String(q.id)
  const out = state.out?.[id] ?? ''
  const set = (v: string | null) => edit({ op: 'set', path: ['out', id], value: v })
  const send = () =>
    post.mutate(
      {
        issue: q.id,
        // The outcome may be an option copied from an issue: inert, like the
        // read-back, so posting it mentions nobody and links no other issue.
        text: `Decided in ${inertForGitHub(plan.title || cycle)}, ${new Date().toISOString().slice(0, 10)}: ${inertForGitHub(out)}`,
      },
      {
        onSuccess: ({ url }) =>
          showToast({
            title: `Posted to #${q.id}`,
            intent: 'success',
            action: {
              label: 'Open on GitHub',
              onClick: () => window.open(url, '_blank', 'noopener'),
            },
          }),
        onError: (err) =>
          showToast({
            // 504: the request reached GitHub and no answer came back.
            title: isAxiosStatus(err, 504) ? 'Not confirmed' : 'Not posted',
            body: apiErrorMessage(err),
            intent: isAxiosStatus(err, 504) ? 'warning' : 'error',
          }),
      },
    )
  return (
    <article
      className={mergeClasses(styles.card, out && styles.decided)}
      data-testid={`question-${q.id}`}
      tabIndex={-1}
      aria-labelledby={`question-title-${q.id}`}
    >
      <div className={styles.top}>
        <IssueLink n={q.id} base={plan.repo_url} />
        {out && (
          <Badge appearance="tint" size="small" color="success">
            Decided
          </Badge>
        )}
      </div>
      <h3 className={styles.title} id={`question-title-${q.id}`}>
        {q.t}
      </h3>
      <IssueNote cycle={cycle} issue={q.id} title={q.t} sum={plan.sums?.[id]?.s} />
      {(q.options ?? []).length > 0 && (
        <div className={styles.options} role="group" aria-label={`Options for ${q.t}`}>
          {(q.options ?? []).map((o) => (
            <button
              key={o.t}
              type="button"
              className={mergeClasses(styles.option, out === o.t && styles.chosen)}
              aria-pressed={out === o.t}
              onClick={() => set(out === o.t ? null : o.t)}
            >
              {o.star && (
                <>
                  <span className={styles.star} aria-hidden="true">
                    ★
                  </span>
                  <span className={styles.srOnly}>Proposed: </span>
                </>
              )}
              {o.t}
            </button>
          ))}
        </div>
      )}
      <div className={styles.foot}>
        <CommitInput
          className={styles.outcome}
          size="small"
          appearance="underline"
          contentBefore={<Gavel16Regular aria-hidden="true" />}
          aria-label={`Decision on ${q.t}`}
          placeholder="Decision"
          value={out}
          onCommit={(v) => set(v || null)}
        />
        {out && (
          <Button
            size="small"
            appearance="subtle"
            icon={<Send16Regular />}
            disabled={post.isPending}
            title={`Post the decision to #${q.id}, signed with your name`}
            onClick={send}
          >
            Post to #{q.id}
          </Button>
        )}
      </div>
    </article>
  )
}

export function DecisionBoard({
  plan,
  state,
  edit,
  cycle = plan.cycle,
}: {
  plan: Plan
  state: PlanState
  edit: (e: PlanEdit) => void
  /** The stored cycle: an imported file may leave its own `cycle` out. */
  cycle?: string
}) {
  const styles = useStyles()
  const panel = usePanelStyles()
  const [draft, setDraft] = useState('')
  const questions = plan.questions ?? []
  const said = state.said ?? []

  return (
    <>
      {questions.length > 0 && (
        <div className={styles.grid}>
          {questions.map((q) => (
            <Question key={q.id} q={q} plan={plan} state={state} edit={edit} cycle={cycle} />
          ))}
        </div>
      )}
      <div className={panel.panel}>
        <div className={panel.panelHead}>
          <Megaphone16Regular aria-hidden="true" />
          Decided in the room
        </div>
        {said.length === 0 ? (
          <Caption1 className={panel.muted}>Nothing yet.</Caption1>
        ) : (
          <ul className={panel.list}>
            {said.map((d) => (
              <li key={d} className={styles.said}>
                <span>{d}</span>
                <Button
                  size="small"
                  appearance="subtle"
                  icon={<Dismiss12Regular />}
                  aria-label={`Remove ${d}`}
                  onClick={() => edit({ op: 'remove', path: ['said'], value: d })}
                />
              </li>
            ))}
          </ul>
        )}
        <form
          className={styles.form}
          onSubmit={(e) => {
            e.preventDefault()
            if (!draft.trim()) return
            edit({ op: 'add', path: ['said'], value: draft.trim() })
            setDraft('')
          }}
        >
          <Input
            size="small"
            className={styles.grow}
            value={draft}
            maxLength={300}
            onChange={(_, d) => setDraft(d.value)}
            placeholder="We decided…"
            aria-label="Add a decision"
          />
          <Button size="small" type="submit" icon={<Add16Regular />} disabled={!draft.trim()}>
            Add
          </Button>
        </form>
      </div>
    </>
  )
}
